import { ScramjetContext } from "@/shared";
import { rewriteUrl, unrewriteUrl, URLMeta } from "@rewriters/url";
import { ScramjetClient } from "@client/index";
import { carrier, carrierNative } from "@client/carrier";
import { Object_setPrototypeOf, JSON_parse } from "@/shared/snapshot";

/** Where the onlineproxy server takes synchronous requests (engine server/syncxhr.js). */
export const SYNC_PATH = "/~/sync";

export default function (client: ScramjetClient, self: Self) {
	const ARGS = Symbol("xhr original args");
	const HEADERS = Symbol("xhr headers");
	const SYNC = Symbol("xhr sync target");
	const { DOMException, encodeURIComponent } = self as any;

	// In a frame outside the service worker (see carrier.ts), the request object is made by the
	// carrier, which is what makes its requests go through the worker; it is still this frame's
	// XMLHttpRequest to the page (prototype, instanceof, hooks).
	client.Proxy("XMLHttpRequest", {
		construct(ctx) {
			const via = carrier(client);
			const ViaXHR = via && carrierNative(via, "XMLHttpRequest");
			if (!ViaXHR) return;
			const xhr = new ViaXHR();
			Object_setPrototypeOf(xhr, (ctx.newTarget as any).prototype);
			ctx.return(xhr);
		},
	});

	// Synchronous requests (open(method, url, false)): Chrome sends them past the service
	// worker, and the page's thread, on which the proxy itself runs, is blocked until the answer
	// is there. They go to the onlineproxy server instead, which makes them with the proxy's
	// cookies (its server/syncxhr.js); Set-Cookie and the final address come back in X-OP-*
	// headers, which the page never sees. They used to be dropped without a request.
	const syncAddress = (url: URL) =>
		client.context.prefix.origin +
		SYNC_PATH +
		"?url=" +
		encodeURIComponent(url.href);
	const isSync = (xhr: any) =>
		(xhr as any)[SYNC] instanceof URL ? ((xhr as any)[SYNC] as URL) : null;
	const syncHeader = (xhr: any, name: string) =>
		client.natives.call(
			"XMLHttpRequest.prototype.getResponseHeader",
			xhr,
			name
		) as string | null;
	// Headers of the answer that are the server's own, not the site's (the server always says
	// no-store and passes the site's Cache-Control as X-OP-Cache-Control).
	const SYNC_OWN =
		/^(x-op-[^:]*|connection|keep-alive|transfer-encoding|cache-control):/i;

	client.Proxy("XMLHttpRequest.prototype.open", {
		apply(ctx) {
			if (ctx.args[2] === undefined) ctx.args[2] = true;
			ctx.this[SYNC] = null;
			if (ctx.args[1]) {
				const rewritten = client.rewriteUrl(ctx.args[1]);
				ctx.args[1] = rewritten;
				if (!ctx.args[2]) {
					let real: URL | null = null;
					try {
						real = new URL(client.unrewriteUrl(rewritten));
					} catch {}
					if (
						real &&
						(real.protocol === "http:" || real.protocol === "https:")
					) {
						ctx.this[SYNC] = real;
						ctx.args[1] = syncAddress(real);
					}
				}
			}
			ctx.this[ARGS] = ctx.args;
		},
	});

	client.Proxy("XMLHttpRequest.prototype.setRequestHeader", {
		apply(ctx) {
			const headers = ctx.this[HEADERS] || (ctx.this[HEADERS] = {});
			headers[ctx.args[0]] = ctx.args[1];
		},
	});

	const siteOf = (host: string) => host.split(".").slice(-2).join(".");

	client.Proxy("XMLHttpRequest.prototype.send", {
		apply(ctx) {
			const real = isSync(ctx.this);
			if (!real) return;
			const xhr = ctx.this;
			const args = xhr[ARGS] || [];
			const method = String(args[0] || "GET").toUpperCase();
			const page = client.url;
			const crossOrigin = real.origin !== page.origin;
			const set = (name: string, value: string) =>
				client.natives.call(
					"XMLHttpRequest.prototype.setRequestHeader",
					xhr,
					name,
					encodeURIComponent(value)
				);
			if (!crossOrigin || xhr.withCredentials) {
				const cookies = client.context.cookieJar.getCookies(
					real,
					false,
					siteOf(real.hostname) === siteOf(page.hostname)
						? "strict"
						: "cross-site"
				);
				if (cookies) set("X-OP-Cookie", cookies);
			}
			// Chrome's default policy, strict-origin-when-cross-origin.
			const referrer = new URL(page.href);
			referrer.hash = "";
			set(
				"X-OP-Referer",
				crossOrigin ? referrer.origin + "/" : referrer.href
			);
			if (crossOrigin || (method !== "GET" && method !== "HEAD"))
				set("X-OP-Origin", page.origin);

			ctx.call();

			const header = (name: string) => syncHeader(xhr, name);
			const setCookies = header("X-OP-Set-Cookie");
			if (setCookies) {
				const synced = [];
				try {
					for (const [url, cookie] of JSON_parse(
						decodeURIComponent(setCookies)
					)) {
						const at = new URL(url);
						client.context.cookieJar.setCookies(cookie, at);
						synced.push({ url: at, cookie });
					}
				} catch {}
				if (synced.length) client.init.sendSetCookie(synced);
			}
			if (header("X-OP-Error")) {
				throw new DOMException(
					`Failed to execute 'send' on 'XMLHttpRequest': Failed to load '${real.href}'.`,
					"NetworkError"
				);
			}
			ctx.return(undefined);
		},
	});

	client.Trap("XMLHttpRequest.prototype.responseURL", {
		get(ctx) {
			const value = ctx.get() as string;
			if (isSync(ctx.this) && value) {
				return syncHeader(ctx.this, "X-OP-URL") || isSync(ctx.this)!.href;
			}
			return client.unrewriteUrl(value);
		},
	});

	client.Proxy("XMLHttpRequest.prototype.getAllResponseHeaders", {
		apply(ctx) {
			const headerstring = ctx.fn.call(ctx.this) as string;
			if (!headerstring) return headerstring;
			let headers = headerstring.split("\r\n");
			if (isSync(ctx.this)) {
				// The site's headers only, not those of the server that made the request.
				const cache = syncHeader(ctx.this, "X-OP-Cache-Control");
				headers = headers.filter((line) => !SYNC_OWN.test(line));
				if (cache) {
					headers.push(`cache-control: ${cache}`);
					headers.sort();
				}
			}

			for (const [i, header] of headers.entries()) {
				if (header.toLowerCase().startsWith("link:")) {
					headers[i] = `Link: ${unrewriteLinkHeader(
						header.slice(5).trim(),
						client.context
					)}`;
				}
			}

			ctx.return(headers.join("\r\n"));
		},
	});
	client.Proxy("XMLHttpRequest.prototype.getResponseHeader", {
		apply(ctx) {
			if (isSync(ctx.this) && SYNC_OWN.test(String(ctx.args[0]) + ":")) {
				return ctx.return(
					/^cache-control$/i.test(String(ctx.args[0]))
						? syncHeader(ctx.this, "X-OP-Cache-Control")
						: null
				);
			}
			const header = ctx.fn.call(ctx.this, ctx.args[0]) as string | null;
			if (!header) return header;
			if (ctx.args[0].toLowerCase() === "link") {
				ctx.return(unrewriteLinkHeader(header, client.context));
			}
		},
	});
}

export function unrewriteLinkHeader(header: string, context: ScramjetContext) {
	return header.replace(
		/<([^>]+)>/gi,
		(_match, p1) => `<${unrewriteUrl(p1, context)}>`
	);
}
